import React from 'react';
import Typography from '@mui/material/Typography';
import MainLayout from 'layouts/MainLayout';
import { useTranslate } from 'hooks/useTranslate';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import styled from '@emotion/styled';
import getLocale from 'helpers/getLocale';
import { JOBALTO_URL, PROFITROLL_URL, PROFITROLL_URL_LANGUAGES } from 'variables/main';
import { COUNTRIES } from 'variables/countries';
import { getCountryFlagAlpha2 } from 'helpers/flags';
import storage from 'helpers/storage';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const ContentBackground = styled('section')(({ theme }) => ({
  backgroundColor: `linear-gradient(to bottom, ${theme.palette.grey[50]}, ${theme.palette.white.main})`
}));

const Content = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main
}));

const RegionSection = styled('div')(() => ({
  marginBottom: 56
}));

const RegionTitle = styled(Typography)(() => ({
  marginBottom: 24
}));

const CountriesWrap = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12
}));

const CountryItem = styled('div')(() => ({
  minWidth: 220,
  marginBottom: 16
}));

const LanguageChip = styled(Typography)(({ theme, active }) => ({
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: 6,
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.palette.primary.main
  }
}));

const Flag = styled('span')(() => ({
  borderRadius: 2,
  marginRight: 6,
  verticalAlign: 'text-bottom'
}));

const CountryName = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.textPrimary.main
}));

const Chips = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 56
}));

const LocalizationsPage = () => {
  const t = useTranslate('Footer');
  const tHelmet = useTranslate('HELMET');
  const locale = getLocale();

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: t('SelectYourLocation')
      }
    ];
  }, [t]);

  const handleChooseLanguage = React.useCallback((lang, countryCode) => {
    let country = '';
    try {
      if (countryCode) {
        country = String(countryCode).toLowerCase();
        storage.setItem('country', country);
      }
    } catch (_) {}

    if (PROFITROLL_URL_LANGUAGES.includes(lang)) {
      window.location.href = `${PROFITROLL_URL}`;
    } else {
      const localePart = lang.toLowerCase();
      const countryPart = country ? `-${country}` : '';
      window.location.href = `https://${JOBALTO_URL}/${localePart}${countryPart}`;
    }
  }, []);

  const getCountryDisplayName = React.useCallback(
    (nameObj) => {
      if (!nameObj || typeof nameObj !== 'object') return '';
      return nameObj[locale] || Object.values(nameObj)[0] || '';
    },
    [locale]
  );

  const regionsFromData = React.useMemo(
    () => Array.from(new Set(COUNTRIES.map((c) => c.region).filter(Boolean))),
    []
  );

  const countriesByRegion = React.useMemo(() => {
    const map = {};
    for (const c of COUNTRIES) {
      if (!c.region) continue;
      (map[c.region] = map[c.region] || []).push(c);
    }
    for (const region of Object.keys(map)) {
      map[region].sort((a, b) => {
        const an = (getCountryDisplayName(a.name) || '').toLowerCase();
        const bn = (getCountryDisplayName(b.name) || '').toLowerCase();
        return an.localeCompare(bn);
      });
    }
    return map;
  }, [getCountryDisplayName]);

  const getCountryAlpha2 = React.useCallback((country) => getCountryFlagAlpha2(country), []);

  const handleScrollToRegion = (region) => {
    const element = document.getElementById(`region-${region}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet
        meta={{
          title: tHelmet('localizationsTitle'),
          description: tHelmet('localizationsDescription'),
          ogTitle: tHelmet('localizationsOgTitle'),
          ogDescription: tHelmet('localizationsOgDescription')
        }}
      />
      <MainLayout grey={true}>
        <Header />
        <ContentBackground>
          <Content>
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            <Title variant="h1">{t('SelectYourLocation')}</Title>

            <Chips>
              {regionsFromData.map((region) => (
                <LanguageChip key={`link-${region}`} onClick={() => handleScrollToRegion(region)}>
                  {t(region) || region}
                </LanguageChip>
              ))}
            </Chips>

            {regionsFromData.map((region) => (
                <RegionSection key={region} id={`region-${region}`}>
                  <RegionTitle variant="h3">{t(region)}</RegionTitle>

                  <CountriesWrap>
                    {(countriesByRegion[region] || []).map((country) => (
                      <CountryItem key={getCountryDisplayName(country.name)}>
                        <CountryName variant="body1" sx={{ mb: 1 }}>
                          <Flag className={`fi fi-${getCountryAlpha2(country)}`} />
                          {getCountryDisplayName(country.name)}
                        </CountryName>
                        <CountriesWrap style={{ gap: 8 }}>
                          {(country.language || []).map((lng) => (
                            <LanguageChip
                              key={`${getCountryDisplayName(country.name)}-${lng}`}
                              variant="body2"
                              active={lng === locale}
                              onClick={() => handleChooseLanguage(lng, getCountryAlpha2(country))}>
                              {lng.toUpperCase()}
                            </LanguageChip>
                          ))}
                        </CountriesWrap>
                      </CountryItem>
                    ))}
                  </CountriesWrap>
                </RegionSection>
              ))}
          </Content>
        </ContentBackground>
        <Footer />
      </MainLayout>
    </>
  );
};

export default LocalizationsPage;
